import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useOfferionTranslation } from '../../shared/store/hooks/useOfferionTranslation'
import { Checkbox } from '../ui/Checkbox'
import { isLawInEffect } from '../../shared/utils/dates'

interface Props {
    close: () => void
    officialDate: Date | string
    clientOib: string
    clientName?: string | null
    onSubmit: (shouldFiscalize: boolean) => Promise<void>
    oibDataFound: boolean | null
    initialShouldNotFiscalize: boolean
    showNewFiscalizeMessage: boolean
    eInvoiceSent?: boolean
    numberFormatError?: boolean
}

export const ConfirmFiscalizationModal = ({
    close,
    onSubmit,
    officialDate,
    clientOib,
    oibDataFound,
    initialShouldNotFiscalize,
    showNewFiscalizeMessage,
    clientName,
    eInvoiceSent = false,
    numberFormatError = false,
}: Props) => {
    const { t } = useOfferionTranslation()
    const [isLoading] = useState(false)

    const dayJsOfficialDate = dayjs(officialDate).add(3, 'day').startOf('day')
    const today = dayjs(new Date())
    const canFiscalize = dayJsOfficialDate.isAfter(today)
    const showFiscalizeOption = canFiscalize && !eInvoiceSent
    const isAfterLawChange = isLawInEffect()

    // After 01.01.2026, when OIB data is found, default to "don't fiscalize"
    const [shouldCheckByDefault] = useState(isAfterLawChange && oibDataFound ? true : initialShouldNotFiscalize)

    const showNoOibInfo = oibDataFound === false

    return (
        <div className="popup__overlay popup__overlay--show send-document-by-email">
            <div className="popup">
                <h1 className="overlay__title">{t('Invoice.Print.Fiscalisation.pageTitle')}</h1>
                <div className="popup__close icono-cross" onClick={close}>
                    {t('Allaround.Popup.close')}
                </div>

                {isLoading ? (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
                ) : (
                    <Formik
                        initialValues={{ shouldNotFiscalize: shouldCheckByDefault }}
                        onSubmit={(val, ctx) =>
                            onSubmit(!val.shouldNotFiscalize).catch(() => {
                                if (!val.shouldNotFiscalize) ctx.setFieldValue('shouldNotFiscalize', true)
                            })
                        }
                    >
                        {formikProps => (
                            <Form>
                                <div className="address-placeholder">
                                    {showFiscalizeOption ? (
                                        <>
                                            <div className="info__row">{t('Invoice.Print.Fiscalisation.questionYesNo')}</div>

                                            {numberFormatError ? (
                                                <div className="info__row" style={{ marginTop: 6, color: 'red' }}>
                                                    <small>{t('Invoice.Print.Fiscalisation.invalidNumberFormat')}</small>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="info__row flex">
                                                        <Checkbox name="shouldNotFiscalize" disabled={!canFiscalize} />
                                                        <div className="ml1">
                                                            <label htmlFor="shouldNotFiscalize">
                                                                {t('Invoice.Print.Fiscalisation.printDontFiscalize')}
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {showNoOibInfo ? (
                                                        <div className="info__row" style={{ marginTop: 6 }}>
                                                            <small>{t('Invoice.Print.Fiscalisation.messageNoOibFound')}</small>
                                                        </div>
                                                    ) : oibDataFound ? (
                                                        <div className="info__row" style={{ marginTop: 6 }}>
                                                            <small>{t('Invoice.Print.Fiscalisation.messageOibCroatianCompany')}</small>
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="info__row">
                                            {!eInvoiceSent && (
                                                <>
                                                    {t('Allaround.Validation.Fiscalization.generalIntro')}{' '}
                                                    {t('Allaround.Validation.Fiscalization.tooLate')}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="popup__buttons mod-margin-top-8">
                                    <button className="button button--gray popup__button" type="button" onClick={close}>
                                        <span className="icon icon--small icon__cross-red"></span>
                                        {t('Allaround.Button.cancel')}
                                    </button>
                                    <button className="button button--gray popup__button" type="submit">
                                        <span className="icon icon__check-green"></span>
                                        {t('Allaround.Button.print')}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                )}
            </div>
        </div>
    )
}
